import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import {useFormik} from 'formik';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {SketchPicker} from 'react-color';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {AUTO_REFRESH_SELECT_ITEMS} from '../../constants/common';
import {CONFIGURATION_SCOPES} from '../../constants/settings';
import {useAppDispatch} from '../../hooks/redux';
import {
  ConfigurationInput,
  ConfigurationResponse,
} from '../../interfaces/Configuration';
import reduxActions from '../../redux/actions';
import {COLORED_ICONS} from '../../utils/icons';
import {tweakLabelForMiner} from '../../utils/macAddress';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import NumberTextField from '../common/NumberTextField';
import SnackbarMessages from '../common/SnackbarMessages';
import {ColorSelect} from '../selectors/ColorSelect';
import {SoundSelect} from '../selectors/SoundSelect';

interface ConfigurationCommtracValues {
  [key: string]: string | number | [number, string][] | null;
}

const CONFIGURATION_SECTIONS = [
  'hazard-ai',
  'hazard-ai-color',
  'hazard-ai-sound',
  'hazard-ai-heatmap',
];

const FIELDS_BY_SECTIONS = {
  communicationStatus: ['hazard-ai.hazard_ai_no_heartbeat_period'],
  autoRefresh: [
    'hazard-ai.hazard_ai_autorefresh_rate',
    'hazard-ai.hazard_ai_event_autorefresh_rate',
  ],

  hazardAIPanel: ['hazard-ai.hazard_ai_hide_warnings'],

  zoneOverride: [
    'hazard-ai.hazard_ai_zone_override_enabled',
    'hazard-ai.hazard_ai_zone_hazard_distance',
    'hazard-ai.hazard_ai_zone_warning_distance',
    'hazard-ai.hazard_ai_zone_id_distance',
  ],

  soundGlobal: ['hazard-ai.sound_new_event'],

  eventSound: [
    'hazard-ai-sound.am_sound_801',
    'hazard-ai-sound.am_sound_802',
    'hazard-ai-sound.am_sound_803',
    'hazard-ai-sound.am_sound_804',
    'hazard-ai-sound.am_sound_805',
    'hazard-ai-sound.am_sound_806',
  ],

  eventColor: [
    'hazard-ai-color.ha_color_801',
    'hazard-ai-color.ha_color_802',
    'hazard-ai-color.ha_color_803',
    'hazard-ai-color.ha_color_804',
    'hazard-ai-color.ha_color_805',
    'hazard-ai-color.ha_color_806',
  ],

  heatmapSettings: [
    'hazard-ai-heatmap.density_radius',
    'hazard-ai-heatmap.blur',
    'hazard-ai-heatmap.radius',
    'hazard-ai-heatmap.max',
    'hazard-ai-heatmap.color_gradients',
  ],
};

const FIELDS = _.flatMap(FIELDS_BY_SECTIONS);

const getFormikValues = (
  item?: ConfigurationResponse
): ConfigurationCommtracValues => ({
  ..._.chain(FIELDS)
    .map((name) => ({name, value: null}))
    .keyBy('name')
    .mapValues('value')
    .value(),

  ..._.chain(item)
    .filter((i) => FIELDS.includes(`${i.section}.${i.name}`))
    .keyBy((i) => `${i.section}.${i.name}`)
    .mapValues('value')
    .value(),
});

const getFormikValuesClear = (
  item?: ConfigurationResponse
): ConfigurationCommtracValues => ({
  ..._.chain(item)
    .filter((i) => FIELDS.includes(`${i.section}.${i.name}`))
    .keyBy((i) => `${i.section}.${i.name}`)
    .mapValues('default')
    .value(),
});

export interface AdminHazardAIConfigProps {
  disabled?: boolean;
  scope?: CONFIGURATION_SCOPES;
  onChangeSubmittedInProgress?: (value: boolean) => any;
  onChangeResetInProgress?: (value: boolean) => any;
}

export interface AdminHazardAIConfigRef {
  fetch?: Function;
  submit?: Function;
  reset?: Function;
  clear?: Function;
}

export const AdminHazardAIConfig = forwardRef<
  AdminHazardAIConfigRef,
  React.PropsWithChildren<AdminHazardAIConfigProps>
>(
  (
    {scope, disabled, onChangeSubmittedInProgress, onChangeResetInProgress},
    ref
  ) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();

    const apiUrl = `${apiBaseUrl}/${scope === CONFIGURATION_SCOPES.USER ? 'user-' : ''}configuration`;

    /*******/
    /* ref */
    /*******/
    useImperativeHandle(ref, () => ({
      fetch: () => fetchData(),
      submit: () => formik.handleSubmit(),
      reset: () => handleResetData(),
      clear: () => formik.setValues(getFormikValuesClear(fetchedData)),
    }));

    const reduxDispatch = useAppDispatch();

    /*********/
    /* fetch */
    /*********/
    const [fetchedData, setFetchedData] = useState<ConfigurationResponse>();
    const [fetchedErrors, setFetchedErrors] = useState<string[]>();
    const [fetchedInProgress, setFetchedInProgress] = useState(false);

    const fetchData = async () => {
      setFetchedInProgress(true);

      try {
        const params = {section: JSON.stringify(CONFIGURATION_SECTIONS)};
        const resp = await API.get<ConfigurationResponse>(apiUrl, {params});
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_cfdd3964');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    };

    useEffect(() => {
      fetchData();
      return () => {
        setFetchedData(undefined);
        setFetchedErrors(undefined);
      };
    }, []);

    /**********/
    /* submit */
    /**********/

    const [submittedInProgress, setSubmittedInProgress] = useState(false);

    const submitData = async (data: ConfigurationInput) => {
      setSubmittedInProgress(true);
      try {
        const resp = await API.post<ConfigurationResponse>(apiUrl, data);
        setFetchedData(resp.data);
        reduxDispatch(reduxActions.app.fetchMyConfigurations);
        enqueueSnackbar('Configuration successfully updated', {
          variant: 'success',
          action: CloseSnackbarAction,
        });
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_debd3910');
        enqueueSnackbar(<SnackbarMessages messages={messages} />, {
          variant: 'error',
          action: CloseSnackbarAction,
        });
      }
      setSubmittedInProgress(false);
    };

    /**********/
    /* reset */
    /**********/

    const [resetInProgress, setResetInProgress] = useState(false);

    const resetData = async (data: ConfigurationInput) => {
      setResetInProgress(true);

      try {
        const resp = await API.patch<ConfigurationResponse>(apiUrl, data);
        setFetchedData(resp.data);
        reduxDispatch(reduxActions.app.fetchMyConfigurations);
        enqueueSnackbar('Configuration successfully reset', {
          variant: 'success',
          action: CloseSnackbarAction,
        });
      } catch (error: any) {
        const message = error?.response?.data?.message ?? 'There is an error';
        enqueueSnackbar(message, {
          variant: 'error',
          action: CloseSnackbarAction,
        });
      }
      setResetInProgress(false);
    };

    const handleResetData = async () => {
      const input: ConfigurationInput = {
        section: CONFIGURATION_SECTIONS,
        configuration: _.map(formik.values, (value, key) => ({
          section: key.split('.')[0],
          name: key.split('.')[1],
          value,
        })),
      };
      await resetData(input);
    };

    /*********/
    /* input */
    /*********/
    const inputValidationSchema = yup.object().test(
      'zone-override-ordering',
      'When Zone Distance Overrides are enabled, Hazard must be ≤ Warning ≤ ID distances.',
      function (values) {
        const v = values as Record<string, string | number | null>;
        if (v['hazard-ai.hazard_ai_zone_override_enabled'] !== '1') {
          return true; // validation only applies when the feature is enabled
        }
        const hazard = Number(v['hazard-ai.hazard_ai_zone_hazard_distance'] ?? 10);
        const warning = Number(v['hazard-ai.hazard_ai_zone_warning_distance'] ?? 30);
        const id = Number(v['hazard-ai.hazard_ai_zone_id_distance'] ?? 50);
        if (hazard > warning) {
          return this.createError({
            message: 'Hazard Max Distance must be ≤ Warning Max Distance.',
          });
        }
        if (warning > id) {
          return this.createError({
            message: 'Warning Max Distance must be ≤ ID Max Distance.',
          });
        }
        return true;
      }
    );

    const formik = useFormik({
      initialValues: getFormikValues(fetchedData),
      validationSchema: inputValidationSchema,
      onSubmit: async (values) => {
        const input: ConfigurationInput = {
          company_level: true,
          section: CONFIGURATION_SECTIONS,
          configuration: _.map(values, (value, key) => ({
            section: key.split('.')[0],
            name: key.split('.')[1],
            value,
          })),
        };
        await submitData(input);
      },
    });

    useEffect(() => {
      const newInput = getFormikValues(fetchedData);
      if (!_.isEqual(newInput, formik.values)) {
        formik.setValues(newInput);
      }
    }, [fetchedData]);

    /************/
    /* keysData */
    /************/
    const keysData = useMemo(
      () => ({
        ..._.chain(fetchedData)
          .keyBy((i) => `${i.section}.${i.name}`)
          .mapValues()
          .value(),
      }),
      [fetchedData]
    );

    /************/
    /* colors   */
    /************/
    const [colorPickerIndex, setColorPickerIndex] = useState<number>(-1);
    const [colors, setColors] = useState<[number, string][]>([]);

    useEffect(() => {
      setColors(
        (keysData?.['hazard-ai-heatmap.color_gradients']?.value as unknown as [
          number,
          string,
        ][]) ?? []
      );
    }, [keysData]);

    const handleColorChange = (
      intensity: number,
      color: string,
      index: number
    ) => {
      colors[index] = [intensity, color];
      handleColorsChange(colors);
    };

    const handleColorsChange = (colors: [number, string][]) => {
      setColors(colors);
      formik.setValues({
        ...formik.values,
        'hazard-ai-heatmap.color_gradients': colors,
      });
    };

    // other
    useEffect(() => {
      onChangeSubmittedInProgress?.(submittedInProgress);
    }, [submittedInProgress]);

    useEffect(() => {
      onChangeResetInProgress?.(resetInProgress);
    }, [resetInProgress]);

    const calcColorPickerBackdropHeight = () => {
      const innerBox = document.querySelector(
        '.MuiModal-root > .MuiBox-root > .MuiBox-root'
      );
      return !innerBox ? 'auto' : `${innerBox.clientHeight + 48}px`;
    };

    return (
      <>
        <Box position="relative">
          <Backdrop open={fetchedInProgress}>
            <CircularProgress color="inherit" />
          </Backdrop>

          {fetchedErrors?.map((error, index) => (
            <Alert key={index} severity="error" sx={{mb: 2}}>
              {error}
            </Alert>
          ))}

          <Grid container spacing={4}>
            <Grid item xs={12} lg={6} xl={6}>
              <Paper sx={{p: 3, mb: 3}}>
                <Box fontSize={20} lineHeight={1.6} mb={3}>
                  Auto Refresh Settings
                </Box>

                <Grid container spacing={4}>
                  {FIELDS_BY_SECTIONS.autoRefresh.map((name) => {
                    return (
                      <Grid key={name} item xs={12} lg={6}>
                        <TextField
                          fullWidth
                          label={tweakLabelForMiner(t(keysData[name]?.label))}
                          value={formik.values[name] ?? 'None'}
                          name={name}
                          size="small"
                          type="number"
                          select
                          disabled={disabled}
                          onChange={(event) => {
                            const value =
                              event.target.value === 'None'
                                ? null
                                : event.target.value;
                            formik.setValues({...formik.values, [name]: value});
                          }}
                        >
                          {AUTO_REFRESH_SELECT_ITEMS.map((i) => (
                            <MenuItem key={i ?? ''} value={i ?? ''}>
                              {i ?? 'None'}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6} xl={6}>
              <Paper sx={{p: 3, mb: 3}}>
                <Box fontSize={20} lineHeight={1.6} mb={3}>
                  Communication Status
                </Box>

                <Grid container spacing={4}>
                  {FIELDS_BY_SECTIONS.communicationStatus.map((name) => {
                    return (
                      <Grid key={name} item xs={12} lg={6}>
                        <Box key={name}>
                          <TextField
                            fullWidth
                            label={t(keysData[name]?.label)}
                            value={formik.values[name] ?? ''}
                            name={name}
                            type="number"
                            size="small"
                            disabled={disabled}
                            onChange={(event) =>
                              formik.setValues({
                                ...formik.values,
                                [name]: event.target.value,
                              })
                            }
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={4}>
            <Grid item xs={12} lg={6} xl={6}>
              <Paper sx={{p: 3, mb: 3}}>
                <Box fontSize={20} lineHeight={1.6} mb={3}>
                  Default Event Sounds
                </Box>

                <Box mb={3}>
                  <Box fontSize={16} lineHeight={1.6} mb={3}>
                    Global
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      {FIELDS_BY_SECTIONS.soundGlobal
                        .slice(0, 1)
                        .map((name) => (
                          <Box key={name} mb={2}>
                            <SoundSelect
                              fullWidth
                              label={tweakLabelForMiner(
                                t(keysData[name]?.label)
                              )}
                              value={formik.values[name] ?? ''}
                              name={name}
                              size="small"
                              disabled={disabled}
                              onChange={(event) =>
                                formik.setValues({
                                  ...formik.values,
                                  [name]: event.target.value,
                                })
                              }
                            />
                          </Box>
                        ))}
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Box fontSize={16} lineHeight={1.6} mb={3}>
                    Safeye Nodes
                  </Box>

                  <Grid container spacing={3}>
                    {FIELDS_BY_SECTIONS.eventSound.map((name) => (
                      <Grid key={name} item xs={6}>
                        <SoundSelect
                          fullWidth
                          label={tweakLabelForMiner(t(keysData[name]?.label))}
                          value={formik.values[name] ?? ''}
                          name={name}
                          size="small"
                          disabled={disabled}
                          onChange={(event) =>
                            formik.setValues({
                              ...formik.values,
                              [name]: event.target.value,
                            })
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>

              <Paper sx={{p: 3, mb: 3}}>
                <Box fontSize={20} lineHeight={1.6} mb={3}>
                  Hazard AI Panel UI
                </Box>

                <Grid container spacing={4}>
                  <Grid key="hazard-ai-hide-warnings" item xs={12} lg={6}>
                    <TextField
                      fullWidth
                      label="Warnings Tab"
                      value={
                        formik.values['hazard-ai.hazard_ai_hide_warnings'] ??
                        'None'
                      }
                      name="hazard-ai.hazard_ai_hide_warnings"
                      size="small"
                      type="number"
                      select
                      disabled={disabled}
                      onChange={(event) => {
                        const value =
                          event.target.value === 'None'
                            ? null
                            : event.target.value;
                        formik.setValues({
                          ...formik.values,
                          'hazard-ai.hazard_ai_hide_warnings': value,
                        });
                      }}
                    >
                      <MenuItem value="0">Visible</MenuItem>

                      <MenuItem value="1">Hidden</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{p: 3, mb: 3}}>
                <Box fontSize={20} lineHeight={1.6} mb={3}>
                  Detection Zone Distance Overrides
                </Box>

                <Box mb={3} fontSize={14} color="text.secondary">
                  When enabled, each detection&apos;s zone is determined by its
                  distance: within the Hazard distance → Hazard, within the
                  Warning distance → Warning, otherwise → ID. This replaces
                  the zone reported by the device, so distant detections are
                  downgraded from Hazard to Warning or ID as appropriate.
                </Box>

                <Grid container spacing={4}>
                  <Grid item xs={12} lg={6}>
                    <TextField
                      fullWidth
                      label="Zone Distance Overrides"
                      value={
                        formik.values[
                          'hazard-ai.hazard_ai_zone_override_enabled'
                        ] ?? '0'
                      }
                      name="hazard-ai.hazard_ai_zone_override_enabled"
                      size="small"
                      select
                      disabled={disabled}
                      onChange={(event) => {
                        formik.setValues({
                          ...formik.values,
                          'hazard-ai.hazard_ai_zone_override_enabled':
                            event.target.value,
                        });
                      }}
                    >
                      <MenuItem value="0">Disabled</MenuItem>
                      <MenuItem value="1">Enabled</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                {formik.errors[''] && (
                  <Alert severity="error" sx={{mt: 2}}>
                    {formik.errors[''] as string}
                  </Alert>
                )}

                <Box mt={3}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} lg={4}>
                      <NumberTextField
                        fullWidth
                        label="Hazard Max Distance (m)"
                        value={Number(
                          formik.values[
                            'hazard-ai.hazard_ai_zone_hazard_distance'
                          ] ?? 10
                        )}
                        min={0}
                        max={100}
                        step={1}
                        decimalPlaces={0}
                        size="small"
                        disabled={
                          disabled ||
                          formik.values[
                            'hazard-ai.hazard_ai_zone_override_enabled'
                          ] !== '1'
                        }
                        onChange={(value) => {
                          formik.setValues({
                            ...formik.values,
                            'hazard-ai.hazard_ai_zone_hazard_distance': value,
                          });
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} lg={4}>
                      <NumberTextField
                        fullWidth
                        label="Warning Max Distance (m)"
                        value={Number(
                          formik.values[
                            'hazard-ai.hazard_ai_zone_warning_distance'
                          ] ?? 30
                        )}
                        min={0}
                        max={100}
                        step={1}
                        decimalPlaces={0}
                        size="small"
                        disabled={
                          disabled ||
                          formik.values[
                            'hazard-ai.hazard_ai_zone_override_enabled'
                          ] !== '1'
                        }
                        onChange={(value) => {
                          formik.setValues({
                            ...formik.values,
                            'hazard-ai.hazard_ai_zone_warning_distance': value,
                          });
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} lg={4}>
                      <NumberTextField
                        fullWidth
                        label="ID Max Distance (m)"
                        value={Number(
                          formik.values[
                            'hazard-ai.hazard_ai_zone_id_distance'
                          ] ?? 50
                        )}
                        min={0}
                        max={100}
                        step={1}
                        decimalPlaces={0}
                        size="small"
                        disabled={
                          disabled ||
                          formik.values[
                            'hazard-ai.hazard_ai_zone_override_enabled'
                          ] !== '1'
                        }
                        onChange={(value) => {
                          formik.setValues({
                            ...formik.values,
                            'hazard-ai.hazard_ai_zone_id_distance': value,
                          });
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6} xl={6}>
              <Paper sx={{p: 3, mb: 3}}>
                <Box fontSize={20} lineHeight={1.6} mb={3}>
                  Default Event Colors
                </Box>

                <Box>
                  <Grid container spacing={3}>
                    {FIELDS_BY_SECTIONS.eventColor.map((name) => (
                      <Grid key={name} item xs={6}>
                        <ColorSelect
                          fullWidth
                          label={t(keysData[name]?.label)}
                          value={formik.values[name] ?? ''}
                          name={name}
                          size="small"
                          disabled={disabled}
                          icons={COLORED_ICONS.communicationNodes}
                          onChange={(event) =>
                            formik.setValues({
                              ...formik.values,
                              [name]: event.target.value,
                            })
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>

              <Paper sx={{p: 3}}>
                <Box fontSize={20} lineHeight={1.6} mb={3}>
                  Default Heatmap Settings
                </Box>

                <Box mb={3}>
                  <Grid container spacing={3}>
                    {FIELDS_BY_SECTIONS.heatmapSettings
                      .filter(
                        (name) => name !== 'hazard-ai-heatmap.color_gradients'
                      )
                      .map((name) => {
                        return (
                          <Grid key={name} item xs={6}>
                            <NumberTextField
                              fullWidth
                              label={t(keysData[name]?.label)}
                              value={Number(
                                ((formik.values[name] as number) ?? 0).toFixed(
                                  name === 'hazard-ai-heatmap.max' ? 3 : 0
                                )
                              )}
                              min={0}
                              step={
                                name === 'hazard-ai-heatmap.max' ? 0.001 : 1
                              }
                              decimalPlaces={
                                name === 'hazard-ai-heatmap.max' ? 3 : 0
                              }
                              size="small"
                              onChange={(value) => {
                                formik.setValues({
                                  ...formik.values,
                                  [name]: value,
                                });
                              }}
                            />
                          </Grid>
                        );
                      })}
                  </Grid>
                </Box>
                <Divider />
                <Box mt={3}>
                  <Grid container spacing={3}>
                    <Grid item xs={1} />
                    <Grid item xs={5}>
                      Intensity
                    </Grid>
                    <Grid item xs={6}>
                      Color
                    </Grid>
                    {colors?.map((intCol: [number, string], index) => (
                      <>
                        <Grid item xs={1}>
                          <IconButton
                            size="small"
                            sx={{height: 40, width: 40}}
                            onClick={() =>
                              handleColorsChange(
                                colors.filter((c) => c !== intCol)
                              )
                            }
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                        <Grid item xs={5}>
                          <NumberTextField
                            fullWidth
                            size="small"
                            max={1}
                            min={0}
                            step={0.1}
                            value={Number(intCol[0].toFixed(1))}
                            onChange={(v) =>
                              handleColorChange(v as number, intCol[1], index)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} position="relative">
                          <Box
                            width={36}
                            height={36}
                            borderRadius={18}
                            onClick={() => setColorPickerIndex(index)}
                            sx={{
                              backgroundColor: intCol[1],
                              cursor: 'pointer',
                            }}
                          />
                          {colorPickerIndex === index && (
                            <>
                              <Backdrop
                                open
                                onClick={() => setColorPickerIndex(-1)}
                                sx={{
                                  height: calcColorPickerBackdropHeight(),
                                  backgroundColor: 'transparent',
                                  zIndex: 1,
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: '45px',
                                  zIndex: 2,
                                }}
                              >
                                <SketchPicker
                                  color={intCol[1]}
                                  onChangeComplete={(color) =>
                                    handleColorChange(
                                      intCol[0],
                                      color.hex,
                                      index
                                    )
                                  }
                                />
                              </Box>
                            </>
                          )}
                        </Grid>
                      </>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() =>
                          handleColorsChange([...colors, [1, '#f00']])
                        }
                      >
                        <AddIcon />
                        Add New Row
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </>
    );
  }
);
